import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPaciente,
  getPaciente,
  listPacientes,
  updatePaciente,
  type CreatePacientePayload,
} from '@/lib/pacientes';
import {
  createAtendimento,
  getAtendimento,
  listAtendimentos,
  type CreateAtendimentoPayload,
} from '@/lib/atendimentos';
import {
  assinarLaudo,
  cancelarLaudo,
  createLaudo,
  getLaudo,
  listLaudos,
  updateLaudo,
} from '@/lib/laudos';
import {
  completeUpload,
  deleteMidia,
  getDownloadUrl,
  listMidias,
  requestUpload,
  uploadToBlob,
  type Midia,
  type TipoMidia,
} from '@/lib/midia';
import { listNotifications, markAllRead, markRead } from '@/lib/notifications';

// ---- Pacientes ----
export const usePacientes = (q?: string) =>
  useQuery({ queryKey: ['pacientes', q], queryFn: () => listPacientes(q) });

export const usePaciente = (id?: string) =>
  useQuery({ queryKey: ['paciente', id], queryFn: () => getPaciente(id!), enabled: !!id });

export const useCreatePaciente = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePacientePayload) => createPaciente(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pacientes'] }),
  });
};

export const useUpdatePaciente = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updatePaciente>[1] }) =>
      updatePaciente(id, data),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['paciente', v.id] });
      qc.invalidateQueries({ queryKey: ['pacientes'] });
    },
  });
};

// ---- Atendimentos ----
export const useAtendimentos = (pacienteId?: string) =>
  useQuery({
    queryKey: ['atendimentos', pacienteId],
    queryFn: () => listAtendimentos(pacienteId),
  });

export const useAtendimento = (id?: string) =>
  useQuery({ queryKey: ['atendimento', id], queryFn: () => getAtendimento(id!), enabled: !!id });

export const useCreateAtendimento = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAtendimentoPayload) => createAtendimento(data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['atendimentos'] });
      qc.invalidateQueries({ queryKey: ['atendimentos', vars.pacienteId] });
    },
  });
};

// ---- Laudos ----
export const useLaudos = (params: Parameters<typeof listLaudos>[0] = {}) =>
  useQuery({ queryKey: ['laudos', params], queryFn: () => listLaudos(params) });

export const useLaudo = (id?: string) =>
  useQuery({ queryKey: ['laudo', id], queryFn: () => getLaudo(id!), enabled: !!id });

export const useCreateLaudo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createLaudo,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['laudos'] }),
  });
};

export const useUpdateLaudo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateLaudo>[1] }) =>
      updateLaudo(id, data),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['laudo', v.id] });
      qc.invalidateQueries({ queryKey: ['laudos'] });
    },
  });
};

export const useAssinarLaudo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, crm }: { id: string; crm?: string }) => assinarLaudo(id, crm),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['laudo', v.id] });
      qc.invalidateQueries({ queryKey: ['laudos'] });
    },
  });
};

export const useCancelarLaudo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelarLaudo(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['laudos'] }),
  });
};

// ---- Mídia ----
export const useMidias = (params: Parameters<typeof listMidias>[0] = {}) =>
  useQuery({ queryKey: ['midia', params], queryFn: () => listMidias(params) });

export const useUploadMidia = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      file: File;
      tipo: TipoMidia;
      pacienteId?: string;
      atendimentoId?: string;
      cirurgiaId?: string;
      titulo?: string;
      descricao?: string;
      tagsCsv?: string;
      onProgress?: (pct: number) => void;
    }) => {
      const grant = await requestUpload({
        tipo: args.tipo,
        nomeOriginal: args.file.name,
        mimeType: args.file.type || 'application/octet-stream',
        tamanhoBytes: args.file.size,
        pacienteId: args.pacienteId,
        atendimentoId: args.atendimentoId,
        cirurgiaId: args.cirurgiaId,
        titulo: args.titulo,
        descricao: args.descricao,
        tagsCsv: args.tagsCsv,
      });
      await uploadToBlob(args.file, grant.uploadUrl);
      await completeUpload(grant.midiaId);
      return grant.midiaId;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['midia'] }),
  });
};

export const useDeleteMidia = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteMidia,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['midia'] }),
  });
};

export async function openDownload(midia: Midia) {
  const { url } = await getDownloadUrl(midia.id);
  window.open(url, '_blank', 'noopener');
}

// ---- Notificações ----
export const useNotifications = (unread = false) =>
  useQuery({
    queryKey: ['notifications', unread],
    queryFn: () => listNotifications(unread),
    // Fallback: as novas chegam pelo NotificationHub (useNotificationHub invalida esta query).
    // O polling só cobre o caso de o hub estar fora — daí o intervalo longo.
    refetchInterval: 5 * 60_000,
  });

export const useMarkRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
};

export const useMarkAllRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
};
